import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Mic,
  MicVocal,
  MonitorDot,
  Sparkles,
  Video,
  Link as LinkIcon,
  Volume2,
  ChevronLeft,
  ChevronRight,
  Speech,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ActionType } from '../../../../types';
import Upload from '../../../upload';
import { initializeRecorder } from '../../../recording/recordingSlice';
import { RouteParams } from '../../types';
import { pushModal } from '../../../globalModals/globalModalsSlice';
import { openDictationModal } from '../../../dictation/dictationSlice';
import { RootState } from '../../../../reducers';
import { intents } from './intents';
import { trackMediaAction } from './mediaActionsSlice';

type LibraryActionKeyType =
  | 'upload'
  | 'record-audio'
  | 'record-tab-audio'
  | 'record-screen'
  | 'import-from-url'
  | 'record-video'
  | 'instant-meeting'
  | 'new-dictation'
  | 'text-to-speech';

interface ExtendedActionType<T> extends ActionType<T> {
  tooltip: string;
  comingSoon?: boolean;
}

type MediaActionType = ExtendedActionType<LibraryActionKeyType>;

const desktopActions: MediaActionType[] = [
  {
    key: 'import-from-url',
    label: 'Import URL',
    tooltip: 'Add media from an online source',
    startIcon: <LinkIcon />,
  },
  {
    key: 'record-screen',
    label: 'Record Screen',
    tooltip: 'Record screen activity for meetings or presentations',
    startIcon: <MonitorDot />,
  },
  {
    key: 'record-audio',
    label: 'Record Microphone',
    tooltip: 'Record audio for meetings, lectures, or interviews',
    startIcon: <Mic />,
  },
  {
    key: 'new-dictation',
    label: 'Dictate',
    tooltip: 'Quickly transcribe audio, ask a question',
    startIcon: <MicVocal />,
  },
  {
    key: 'record-tab-audio',
    label: 'Record Tab Audio',
    tooltip: 'Record from Browser tab',
    startIcon: <Volume2 />,
  },
  {
    key: 'instant-meeting',
    label: 'Instant Meeting',
    tooltip: 'Video call with auto-recording to your library',
    startIcon: <Video />,
  },
  {
    key: 'text-to-speech',
    label: 'Text to Speech',
    tooltip: 'Convert documents to audio',
    startIcon: <Speech />,
  },
];

const mobileActions: MediaActionType[] = [
  {
    key: 'record-audio',
    label: 'Record Microphone',
    tooltip: 'Record audio for meetings, lectures, or interviews',
    startIcon: <Mic />,
  },
  {
    key: 'new-dictation',
    label: 'Dictate',
    tooltip: 'Quickly transcribe audio, ask a question',
    startIcon: <MicVocal />,
  },
  {
    key: 'import-from-url',
    label: 'Import URL',
    tooltip: 'Add media from an online source',
    startIcon: <LinkIcon />,
  },
];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 600);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const MediaActions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { teamId, folderId } = useParams<RouteParams>();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);
  const { status } = useSelector((state: RootState) => ({
    status: state.import.status as 'uploading' | 'idle' | 'error' | 'success',
  }));

  const intent = useSelector((state: RootState) => state.plan.marketing.intent);
  const isMobile = useIsMobile();

  const translatedDesktopActions = React.useMemo(
    () =>
      desktopActions.map((action) => ({
        ...action,
        label: t(`mediaActions.actions.${action.key}.label`, action.label),
        tooltip: t(
          `mediaActions.actions.${action.key}.tooltip`,
          action.tooltip,
        ),
      })),
    [t],
  );

  const translatedMobileActions = React.useMemo(
    () =>
      mobileActions.map((action) => ({
        ...action,
        label: t(`mediaActions.actions.${action.key}.label`, action.label),
        tooltip: t(
          `mediaActions.actions.${action.key}.tooltip`,
          action.tooltip,
        ),
      })),
    [t],
  );

  const allActions = React.useMemo(() => {
    const baseActions = isMobile
      ? translatedMobileActions
      : translatedDesktopActions;

    return baseActions;
  }, [isMobile, translatedDesktopActions, translatedMobileActions]);

  const handleAction = React.useCallback(
    async (
      actionKey: LibraryActionKeyType,
      options?: { dictationMode?: 'fast' | 'best' | 'answer' },
    ) => {
      console.log('handleAction called with:', actionKey, {
        teamId,
        folderId,
        status,
      });
      if (status === 'uploading') return;

      if (teamId && folderId) {
        dispatch(
          trackMediaAction({
            actionKey: actionKey.toString(),
            teamId,
            folderId,
          }),
        );
      }

      if (actionKey === 'record-audio') {
        console.log('Record audio clicked', { teamId, folderId });
        if (teamId && folderId) {
          console.log('Initializing audio recorder');
          const settings = {
            audioSettings: {
              microphoneAudio: true,
              browserAudio: true,
              isMicrophoneOnly: true,
            },
            videoSettings: { screen: false, webCam: false },
          };
          dispatch(initializeRecorder({ teamId, folderId, settings }));
        } else {
          console.log('Missing teamId or folderId', { teamId, folderId });
        }
      } else if (actionKey === 'record-tab-audio') {
        console.log('Record tab audio clicked', { teamId, folderId });
        if (teamId && folderId) {
          console.log('Initializing tab audio recorder');
          const settings = {
            audioSettings: {
              microphoneAudio: true,
              browserAudio: true,
              isTabAudioOnly: true,
            },
            videoSettings: {
              screen: true,
              webCam: false,
            },
          };
          await dispatch(initializeRecorder({ teamId, folderId, settings }));
        } else {
          console.log('Missing teamId or folderId', { teamId, folderId });
        }
      } else if (actionKey === 'record-screen') {
        console.log('Record screen clicked', { teamId, folderId });
        if (teamId && folderId) {
          console.log('Initializing screen recorder');
          const settings = {
            audioSettings: { microphoneAudio: true, browserAudio: true },
            videoSettings: { screen: true, webCam: true },
          };
          dispatch(initializeRecorder({ teamId, folderId, settings }));
        } else {
          console.log('Missing teamId or folderId', { teamId, folderId });
        }
      } else if (actionKey === 'import-from-url') {
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          }),
        );
      } else if (actionKey === 'instant-meeting') {
        navigate('/tool/p2pmeeting', { state: { fromMediaActions: true } });
      } else if (actionKey === 'new-dictation') {
        if (options?.dictationMode) {
          dispatch(openDictationModal(options.dictationMode));
        } else {
          dispatch(openDictationModal());
        }
      } else if (actionKey === 'text-to-speech') {
        dispatch(
          pushModal({
            type: 'text-to-speech-modal',
          }),
        );
      }
    },
    [dispatch, status, teamId, folderId, navigate],
  );

  const currentIntent = React.useMemo(
    () => (intent ? intents.find((i) => i.key === intent) : null),
    [intent],
  );

  const handleIntentClick = React.useCallback(() => {
    if (!currentIntent?.action) return;

    const options = currentIntent.dictationMode
      ? { dictationMode: currentIntent.dictationMode }
      : undefined;

    handleAction(currentIntent.action as LibraryActionKeyType, options);
  }, [currentIntent, handleAction]);

  const checkScroll = React.useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  React.useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      return () => container.removeEventListener('scroll', checkScroll);
    }
    return undefined;
  }, [checkScroll, allActions.length]);

  React.useEffect(() => {
    const handleResize = () => checkScroll();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkScroll]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <div
      className={cn('w-full', 'flex items-center justify-center', 'relative')}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        ref={scrollContainerRef}
        className="w-full overflow-x-auto scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <div
          className={cn(
            'flex gap-1',
            'w-max', // Always left-aligned
            'py-1',
            'pl-2', // Align with library content
          )}
        >
          {currentIntent && (
            <div className="flex flex-col items-center min-w-[130px] flex-shrink-0 ml-1">
              <Button
                onClick={handleIntentClick}
                variant="default"
                className="w-[120px] h-[75px] rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-all duration-[400ms] ease-in-out transform hover:scale-[1.02] flex flex-col items-center justify-center gap-1.5 px-3 group"
              >
                {currentIntent?.icon ? (
                  <currentIntent.icon
                    size={24}
                    className="w-6 h-6 transition-transform duration-[400ms] ease-in-out group-hover:scale-110"
                    style={{ width: '24px', height: '24px' }}
                  />
                ) : (
                  <Sparkles
                    size={24}
                    className="w-6 h-6 transition-transform duration-[400ms] ease-in-out group-hover:scale-110"
                    style={{ width: '24px', height: '24px' }}
                  />
                )}
                <span className="text-xs font-medium whitespace-nowrap transition-all duration-[400ms] ease-in-out">
                  {currentIntent.label || intent}
                </span>
              </Button>
            </div>
          )}
          <Upload />
          {allActions.map(({ key, label, startIcon }) => {
            const displayLabel = label;

            // Map action keys to feature IDs for onboarding tooltips
            const featureIdMap: Record<string, string> = {
              'import-from-url': 'feature-import-url',
              'record-audio': 'feature-record-audio',
              'new-dictation': 'feature-new-dictation',
            };
            const featureId = featureIdMap[key];

            return (
              <div
                key={key}
                id={featureId}
                className="flex flex-col items-center min-w-[130px] flex-shrink-0"
              >
                <Button
                  onClick={() => handleAction(key)}
                  variant="ghost"
                  className="w-[120px] h-[75px] rounded-lg bg-white dark:bg-secondary border border-gray-100 dark:border-border text-gray-700 dark:text-gray-200 hover:bg-blue-500 hover:text-white hover:border-transparent transition-all duration-[400ms] ease-in-out transform hover:scale-[1.02] flex flex-col items-center justify-center gap-1.5 px-3 group"
                >
                  {React.cloneElement(startIcon as React.ReactElement, {
                    size: 24,
                    className:
                      'w-6 h-6 transition-transform duration-[400ms] ease-in-out group-hover:scale-110',
                    style: { width: '24px', height: '24px' },
                  })}
                  <span className="text-xs font-medium whitespace-nowrap transition-all duration-[400ms] ease-in-out">
                    {displayLabel}
                  </span>
                </Button>
              </div>
            );
          })}
          {/* Right spacer for scrolling space */}
          <div className="w-1 flex-shrink-0" />
        </div>
      </div>

      {/* Left Chevron */}
      <Button
        onClick={scrollLeft}
        size="icon"
        variant="ghost"
        className={cn(
          'absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 dark:bg-secondary/90 backdrop-blur-sm border border-gray-100 dark:border-border hover:bg-blue-500 hover:text-white hover:border-transparent',
          'transition-all duration-[400ms] ease-in-out transform hover:scale-110',
          canScrollLeft && isHovering
            ? 'opacity-100 translate-x-0'
            : 'opacity-0 -translate-x-4 pointer-events-none',
        )}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>

      {/* Right Chevron */}
      <Button
        onClick={scrollRight}
        size="icon"
        variant="ghost"
        className={cn(
          'absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 dark:bg-secondary/90 backdrop-blur-sm border border-gray-100 dark:border-border hover:bg-blue-500 hover:text-white hover:border-transparent',
          'transition-all duration-[400ms] ease-in-out transform hover:scale-110',
          canScrollRight && isHovering
            ? 'opacity-100 translate-x-0'
            : 'opacity-0 translate-x-4 pointer-events-none',
        )}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default React.memo(MediaActions);
